// Shared vocabulary of the Team section (list, cards, forms, dialogs).
//
// The Team pages take the Team portal's accent — the indigo the sidebar lights
// the intranet with — so the section reads as one place with the rail and the
// dashboard. Every member wears the Group gradient as an avatar ring, the way
// the account card in the rail does; nothing else on these pages is a gradient.
import { alpha } from '@mui/material/styles';
import {
  BRAND_FONT,
  GROUP_GRADIENT,
  INDIGO,
  INDIGO_BRIGHT,
  INDIGO_INK
} from 'src/layouts/auth/portals';

// ----------------------------------------------------------------------

// THE FIELD GRID OF EVERY FORM AND FORM-LIKE DIALOG. It lived in
// formPieces.js, which also pulls in the password-strength widgets: a dialog
// that wanted nothing but the grid dragged those in with it, and a Jest suite
// that touched such a dialog died on their ESM build. Two frozen objects
// belong with the other tokens.
export const FIELDS_SX = {
  display: 'grid',
  columnGap: 2,
  rowGap: 2.5,
  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }
};
export const FULL_ROW = { gridColumn: { sm: '1 / -1' } };

export const TEAM_ACCENT = INDIGO;
export const TEAM_ACCENT_INK = INDIGO_INK;
export const TEAM_ACCENT_BRIGHT = INDIGO_BRIGHT;
export const TEAM_FONT = BRAND_FONT;

// The one list of departments. The cards page used to group by six while the
// form only offered four, so a Marketing or Finance hire could not be created
// from the form that the cards page then grouped them under.
// Each department gets the symbol of its trade — a board for projects, a
// handshake for accounts, a loudspeaker for marketing, a banknote for
// finance, code for software, a rocket for new business — not a generic
// chart or widget.
export const DEPARTMENTS = [
  { name: 'Project Management', icon: 'solar:kanban-bold-duotone' },
  { name: 'Account Management', icon: 'solar:hand-shake-bold-duotone' },
  { name: 'Marketing', icon: 'solar:volume-loud-bold-duotone' },
  { name: 'Finance', icon: 'solar:banknote-bold-duotone' },
  { name: 'Software Development', icon: 'solar:code-square-bold-duotone' },
  { name: 'Business Development', icon: 'solar:rocket-bold-duotone' }
];

export const DEPARTMENT_NAMES = DEPARTMENTS.map((d) => d.name);

export const OTHER_DEPARTMENT = 'Other';

export const departmentIcon = (name) =>
  DEPARTMENTS.find((d) => d.name === name)?.icon ||
  'solar:buildings-2-bold-duotone';

export const ROLES = [
  'admin',
  'Senior Manager',
  'Account Manager',
  'Project Manager',
  'Finance Manager',
  'Marketing Manager',
  'Software Developer',
  'HR Manager',
  'Business Developer'
];

// ----------------------------------------------------------------------
// Colour helpers. Light mode uses the accent and its ink; dark mode the bright
// step, which is what the rail does too.

export const accentFor = (theme) =>
  theme.palette.mode === 'light' ? TEAM_ACCENT : TEAM_ACCENT_BRIGHT;

export const accentInkFor = (theme) =>
  theme.palette.mode === 'light' ? TEAM_ACCENT_INK : TEAM_ACCENT_BRIGHT;

const tint = (theme, light, dark) =>
  alpha(TEAM_ACCENT, theme.palette.mode === 'light' ? light : dark);

// ----------------------------------------------------------------------
// Buttons. Solid accent for the one primary action of a page, an outlined
// hairline for the way back, a soft tint for the action every card carries.

export const teamPrimaryButtonSx = {
  borderRadius: 1.25,
  px: 2.5,
  fontFamily: TEAM_FONT,
  fontWeight: 600,
  textTransform: 'none',
  color: '#FFFFFF',
  bgcolor: TEAM_ACCENT,
  boxShadow: `0 8px 18px ${alpha(TEAM_ACCENT, 0.32)}`,
  '&:hover': {
    bgcolor: TEAM_ACCENT_INK,
    boxShadow: `0 10px 22px ${alpha(TEAM_ACCENT, 0.4)}`
  },
  '&.Mui-disabled': {
    color: '#FFFFFF',
    // Opacity, not a paler tint: a 40% indigo over a dark card read as enabled.
    bgcolor: TEAM_ACCENT,
    opacity: 0.45,
    boxShadow: 'none'
  }
};

export const teamSecondaryButtonSx = (theme) => ({
  borderRadius: 1.25,
  px: 2.25,
  fontFamily: TEAM_FONT,
  fontWeight: 600,
  textTransform: 'none',
  color: theme.palette.text.primary,
  border: `1px solid ${alpha(theme.palette.text.primary, 0.16)}`,
  '&:hover': {
    bgcolor: alpha(theme.palette.text.primary, 0.04),
    borderColor: alpha(theme.palette.text.primary, 0.32)
  }
});

export const teamSoftButtonSx = (theme) => ({
  borderRadius: 1.25,
  fontFamily: TEAM_FONT,
  fontWeight: 600,
  textTransform: 'none',
  color: accentInkFor(theme),
  bgcolor: tint(theme, 0.1, 0.18),
  boxShadow: 'none',
  '&:hover': { bgcolor: tint(theme, 0.18, 0.28), boxShadow: 'none' }
});

/**
 * A soft button in a SEMANTIC colour, for a control that is also a state
 * (report 05/10/2026, §11): the indigo accent while the thing has not been
 * done, amber once it is out and waiting, green once it is answered. Same
 * shape as teamSoftButtonSx in every other respect, and `default` IS
 * teamSoftButtonSx, so a plain action is unchanged.
 *
 * The colour is never the only signal: the callers change the label too.
 */
export const teamToneButtonSx = (theme, tone = 'default') => {
  const base = teamSoftButtonSx(theme);
  const colour = tone === 'default' ? null : theme.palette[tone];
  if (!colour) return base;
  const light = theme.palette.mode === 'light';
  return {
    ...base,
    color: light ? colour.dark : colour.light,
    bgcolor: alpha(colour.main, light ? 0.12 : 0.22),
    '&:hover': {
      bgcolor: alpha(colour.main, light ? 0.2 : 0.32),
      boxShadow: 'none'
    }
  };
};

// ----------------------------------------------------------------------
// Small parts: the icon tile of a section, the role chip, the avatar ring.

export const iconTileSx = (theme, size = 36) => ({
  width: size,
  height: size,
  flex: 'none',
  borderRadius: 1.25,
  display: 'grid',
  placeItems: 'center',
  color: accentInkFor(theme),
  bgcolor: tint(theme, 0.1, 0.2)
});

export const roleChipSx = (theme) => ({
  color: accentInkFor(theme),
  bgcolor: tint(theme, 0.12, 0.22)
});

// A 2px ring of the Group gradient with a gap of paper between ring and photo.
export const avatarRingSx = (size) => ({
  width: size + 8,
  height: size + 8,
  flex: 'none',
  p: '2px',
  borderRadius: '50%',
  background: GROUP_GRADIENT,
  display: 'grid',
  placeItems: 'center',
  transition: 'box-shadow 200ms',
  '& > span': {
    width: size + 4,
    height: size + 4,
    borderRadius: '50%',
    bgcolor: 'background.paper',
    display: 'grid',
    placeItems: 'center'
  }
});

export const initialsOf = (name = '') =>
  name
    .trim()
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

// A filter pill: outlined at rest, solid accent when on — the Team list's
// department chips, shared by the Tasks and Bits & Bobs pages.
export const teamPillSx = (theme, active) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.75,
  height: 32,
  px: 1.5,
  borderRadius: 999,
  fontFamily: TEAM_FONT,
  fontWeight: 600,
  fontSize: 13,
  lineHeight: 1,
  cursor: 'pointer',
  userSelect: 'none',
  whiteSpace: 'nowrap',
  color: active ? '#FFFFFF' : theme.palette.text.primary,
  bgcolor: active ? TEAM_ACCENT : 'transparent',
  border: `1px solid ${
    active ? TEAM_ACCENT : alpha(theme.palette.text.primary, 0.16)
  }`,
  transition: theme.transitions.create([
    'background-color',
    'color',
    'border-color'
  ]),
  '&:hover': {
    bgcolor: active ? TEAM_ACCENT : alpha(TEAM_ACCENT, 0.1),
    color: active ? '#FFFFFF' : accentInkFor(theme),
    borderColor: TEAM_ACCENT
  },
  '&:focus-visible': {
    outline: `2px solid ${alpha(TEAM_ACCENT, 0.5)}`,
    outlineOffset: 2
  }
});

// An applied filter, removable: the accent tint with the accent ink.
export const teamChipSx = (theme) => ({
  fontWeight: 600,
  color: accentInkFor(theme),
  bgcolor: tint(theme, 0.12, 0.24),
  '& .MuiChip-deleteIcon': {
    color: accentInkFor(theme),
    opacity: 0.7,
    '&:hover': { color: accentInkFor(theme), opacity: 1 }
  }
});
